/**
 * Reusable Button Component
 */

import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'text';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xs';
  isLoading?: boolean;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  className = '',
  disabled = false,
  ...props
}) => {
  const baseStyles =
    'font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

  const variantStyles = {
    primary:
      'bg-pos-primary hover:bg-pos-secondary text-white focus:ring-pos-primary',
    secondary:
      'bg-pos-secondary hover:bg-pos-primary text-white focus:ring-pos-secondary',
    outline:
      'border-2 border-pos-primary text-pos-primary hover:bg-pos-primary hover:text-white focus:ring-pos-primary',
    text: 'bg-transparent hover:bg-gray-100 text-gray-700 focus:ring-gray-300',
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-2.5 text-base',
    xl: 'px-8 py-3 text-lg',
    xs: 'px-1 py-1 text-[10px]',
  };

  const widthStyle = fullWidth ? 'w-full' : '';
  const disabledStyle =
    disabled || isLoading ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={props.type || 'button'}
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${widthStyle} ${disabledStyle} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <span className="flex items-center justify-center gap-1">
          <svg
            className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Loading...
        </span>
      ) : (
        <span className="flex items-center justify-center gap-1">
          {children}
        </span>
      )}
    </button>
  );
};
